import { ABtn } from '@maisaedu/ui-components-a-btn';
import { AInput } from '@maisaedu/ui-components-a-input';
import { ADataTable } from '@maisaedu/ui-components-a-data-table';
import { AAutocomplete } from '@maisaedu/ui-components-a-autocomplete';
import { ATooltip } from '@maisaedu/ui-components-a-tooltip';
import { ACheckbox } from '@maisaedu/ui-components-a-checkbox';
import { mapActions, mapGetters, mapState } from 'vuex';
import { resolveTerm } from '~/helpers/terms';
import { messageContextTypes } from '~/helpers/messageContextTypes';
import { hasPermission } from '@/helpers/permission';
import AcademicMainTypeHelper from '@/helpers/academicMainType';

const LIMIT_PARTICIPANTS_SELECTED_TO_SEND_MESSAGE = Number(process.env.LIMIT_PARTICIPANTS_SELECTED_TO_SEND_MESSAGE) || 500;

export default {
  name  : 'ParticipantManagementModal',
  props : {
    isOpen: {
      type    : Boolean,
      default : false
    },
    academicMainId: {
      type    : Number,
      default : null
    },
    academicMainName: {
      type    : String,
      default : null
    },
    academicMainExternalId: {
      type    : String,
      default : null
    },
    isExpiredCourse: {
      type    : Boolean,
      default : false
    },
    contextType: {
      type    : String,
      default : 'course'
    },
    roleCoursePermissions: {
      type    : [Array, null],
      default : null
    }
  },
  components: {
    ABtn,
    ADataTable,
    AInput,
    AAutocomplete,
    ATooltip,
    ACheckbox
  },
  data() {
    return {
      AcademicMainTypeHelper,
      LIMIT_PARTICIPANTS_SELECTED_TO_SEND_MESSAGE,
      filters: {
        searchTerm : '',
        role       : 0
      },
      totalInstructors         : 0,
      totalParticipants        : 0,
      isLoadingData            : true,
      isSelectedAll            : false,
      currentPageParticipants  : [],
      selectedParticipants     : [],
      loadedParticipants       : [],
      allParticipants          : null,
      totalRows                : 0,
      roles                    : null,
      actionHidesMessageBanner : false,
      tableSettings            : {
        page         : 1,
        itemsPerPage : 10,
        sortBy       : [],
        sortDesc     : [false],
        groupBy      : [],
        groupDesc    : [],
        mustSort     : false,
        multiSort    : false
      },
      allRolesOption : { id: 0, name: this.$t('participantManagement.allParticipantsSelectOption') },
      tableHeaders   : [
        {
          text: this.$t('participantManagement.tableNameHeader'), value: 'name', 'sortable': true
        },
        {
          text     : this.$t('participantManagement.tableProgressHeader'),
          value    : 'studentProgress',
          sortable : true,
          width    : '160px'
        },
        {
          text: this.$t('participantManagement.tableRoleHeader'), value: 'roleName', 'sortable': true
        },
        {
          value    : 'actions',
          sortable : false,
          width    : '90px',
          align    : 'center'
        }
      ],
      showSelectAllBanner : false,
      hasSendMessageBlock : false,
      resolveTerm
    };
  },
  computed: {
    ...mapGetters({
      getUserId    : '_core/user/getUserId',
      getterCourse : 'plataforma/newContent/getterCourse'
    }),
    ...mapState({
      hasInactiveContextLock : state => state.messages.hasInactiveContextLock,
      userPermissions        : state => state.plataforma.courses.userPermissions
    }),
    currentPageSelectAll() {
      return !!this.currentPageParticipants.length && (this.currentPageItems.every(item => this.selectedParticipants.includes(item.safeaUserId) || item.isDisabled));
    },
    hasSomeParticipantSelectedOnCurrentPage() {
      return this.currentPageParticipants.some(item => this.selectedParticipants.includes(item.safeaUserId));
    },
    selectAllBannerMessage() {
      if (this.isSelectedAll) {
        return this.$t('participantManagement.allSelectedMessage');
      }
      if (this.hasReachedSelectedContactsLimit) {
        return this.$t('participantManagement.allSelectedMessageWithLimit');
      }
      if (!this.currentPageSelectAll) {
        return this.$t('participantManagement.otherPagesSelectedMessage');
      }
      return this.$t('participantManagement.currentPageSelectedMessage');
    },
    totalSelectedParticipants() {
      return this.selectedParticipants.filter(item => this.verifyIfIsAllowedToSendMessageToUser(item)).length;
    },
    totalCurrentPageParticipants() {
      return this.currentPageParticipants.filter(item => this.verifyIfIsAllowedToSendMessageToUser(item.safeaUserId)).length;
    },
    hasReachedSelectedContactsLimit() {
      return this.totalSelectedParticipants >= LIMIT_PARTICIPANTS_SELECTED_TO_SEND_MESSAGE;
    },
    currentPageItems() {
      return this.currentPageParticipants.map(item => {
        const isSelected = this.selectedParticipants.includes(item.safeaUserId) && this.verifyIfIsAllowedToSendMessageToUser(item.safeaUserId);
        const isDisabled = !this.verifyIfIsAllowedToSendMessageToUser(item.safeaUserId) || (this.hasReachedSelectedContactsLimit && !isSelected);

        return {
          ...item,
          isSelected,
          isDisabled
        };
      });
    },
    getPermissionsArray() {
      return this.roleCoursePermissions?.length ? this.roleCoursePermissions : this.userPermissions;
    }
  },
  watch: {
    currentPageSelectAll(newValue) {
      if (!newValue || this.actionHidesMessageBanner) {
        return;
      }
      this.showSelectAllBanner = true;
      this.isSelectedAll = this.selectedParticipants.length === parseInt(this.totalParticipants);
    },
    tableSettings(newSettings, oldSettings) {
      const isSortChanged = newSettings.sortBy !== oldSettings.sortBy || newSettings.sortDesc !== oldSettings.sortDesc || newSettings.groupBy !== oldSettings.groupBy || newSettings.groupDesc !== oldSettings.groupDesc;
      if (isSortChanged) {
        this.showSelectAllBanner = false;
      }

      this.getData();
    }
  },
  mounted() {
    this.setupMessageProps();
  },
  methods: {
    hasPermission,
    ...mapActions({
      getRoles        : 'plataforma/courses/getCourseEnrolledRoles',
      getParticipants : 'plataforma/enrollment/actionGetStudents'
    }),
    setupMessageProps() {
      const isAcademicMainExpired = this.getterCourse.context
        ? this.getterCourse.context.isAcademicMainExpired
        : this.isExpiredCourse;
      this.hasSendMessageBlock = isAcademicMainExpired && this.hasInactiveContextLock;
    },
    getData({ getAll, filterByRoleCourse } = { getAll: false, filterByRoleCourse: false }) {
      const data = {
        page         : getAll ? 1 : this.tableSettings.page,
        perPage      : getAll ? this.totalRows : this.tableSettings.itemsPerPage,
        search       : getAll ? null : this.filters.searchTerm,
        roleCourseId : (getAll || this.filters.role === 0) && !filterByRoleCourse ? null : this.filters.role,
        order        : this.tableSettings.sortDesc[0] ? 'DESC' : 'ASC',
        sortBy       : this.tableSettings.sortBy[0],
        fullListing  : true
      };
      this.isLoadingData = true;
      this.$nuxt.$loading.start();
      return this.getParticipants({ data, academicMain: this.academicMainId })
        .then((result) => {
          if (getAll) {
            return result.enrollments;
          }
          this.totalRows = result.totalRows || 0;
          this.totalInstructors = result.totalInstructors;
          this.totalParticipants = result.totalParticipants;
          this.currentPageParticipants = result.enrollments;
          this.loadedParticipants = _.uniqBy([
            ...this.loadedParticipants,
            ...result.enrollments
          ], 'id');
        })
        .catch(() => {
          this.$toast.error(this.$t('course.errorLoadingParticipants'));
        })
        .finally(() => {
          this.isLoadingData = false;
          this.$nuxt.$loading.finish();
        });
    },
    handleFilterChange: _.debounce(function() {
      this.showSelectAllBanner = false;
      this.tableSettings.page = 1;
      this.getData();
    }, 300),
    getRoleOptions() {
      return this.getRoles(this.academicMainId)
        .then(response => {
          return {
            totalCount : response.roles.length,
            items      : _.cloneDeep([this.allRolesOption, ...response.roles])
          };
        })
        .catch(error => {
          this.$toast.error(error.message);
        });
    },
    getSelectedParticipantsByLimit(participantsList) {
      const limitedParticipants = [];

      for (let index = 0; index < participantsList.length; index++) {
        if (this.verifyIfIsAllowedToSendMessageToUser(participantsList[index])) {
          limitedParticipants.push(participantsList[index]);
        }

        if (limitedParticipants.length === LIMIT_PARTICIPANTS_SELECTED_TO_SEND_MESSAGE) {
          break;
        }
      }

      return limitedParticipants;
    },
    toggleIsSelectedAll() {
      this.isSelectedAll = !this.isSelectedAll;
      if (!this.isSelectedAll || this.hasReachedSelectedContactsLimit) {
        this.isSelectedAll = false;
        this.selectedParticipants = [];
        this.showSelectAllBanner = false;
        return;
      }
      if (!!this.allParticipants) {
        this.selectedParticipants = this.getSelectedParticipantsByLimit(_.uniq([
          ...this.selectedParticipants,
          ...this.currentPageItems.map(item => item.safeaUserId),
          ...this.allParticipants.map(item => item.safeaUserId)]
        ));
        return;
      }
      this.getData({ getAll: true, filterByRoleCourse: this.filters.role !== 0 })
        .then(currentPageParticipants => {
          this.allParticipants = currentPageParticipants;
          this.selectedParticipants = this.getSelectedParticipantsByLimit(_.uniq([
            ...this.selectedParticipants,
            ...this.currentPageItems.map(item => item.safeaUserId),
            ...this.allParticipants.map(item => item.safeaUserId)]
          ));
        });
    },
    toggleSelectAll() {
      if (this.currentPageSelectAll) {
        this.selectedParticipants = _.difference(
          this.selectedParticipants,
          this.getSelectedParticipantsByLimit(this.currentPageItems.map(item => item.safeaUserId))
        );
        this.isSelectedAll = false;
        this.showSelectAllBanner = false;
        this.actionHidesMessageBanner = true;
        return;
      }
      this.actionHidesMessageBanner = false;
      this.selectedParticipants = this.getSelectedParticipantsByLimit(_.uniq([
        ...this.selectedParticipants,
        ...this.currentPageItems.map(item => item.safeaUserId)
      ]));
    },
    toggleSelectItem(item, isSelected) {
      if (isSelected) {
        this.selectedParticipants.push(item.safeaUserId);
      } else {
        this.selectedParticipants = _.difference(this.selectedParticipants, [item.safeaUserId]);
      }

      if (this.showSelectAllBanner) {
        this.actionHidesMessageBanner = true;
        this.showSelectAllBanner = false;
        return;
      }
      this.actionHidesMessageBanner = false;
    },
    closeModal() {
      this.$nextTick(() => {
        Object.assign(this.$data, this.$options.data.apply(this));
        this.$emit('close-modal');
      });
    },
    getCourseContext() {
      return {
        contextType  : messageContextTypes.COURSE,
        externalId   : String(this.academicMainId),
        name         : this.academicMainName,
        externalCode : this.academicMainExternalId
      };
    },
    parseParticipantToMessageContact(participant) {
      return {
        name           : participant.name,
        email          : participant.email,
        safeaRole      : participant.safeaRole,
        roleAlias      : participant.safeaRole,
        externalUserId : String(participant.safeaUserId),
        imagePath      : participant.profilePhoto || null,
        contexts       : [this.getCourseContext()]
      };
    },
    onSendSingleParticipantMessage(participantInfo) {
      const selectedParticipant = [this.parseParticipantToMessageContact(participantInfo)];
      this.handleSendMessage(selectedParticipant);
    },
    onSendMessageClick() {
      const selectedContacts = [];
      const participantReferenceList = this.isSelectedAll ? this.allParticipants : this.loadedParticipants;
      for (const participantSafeaUserId of this.selectedParticipants) {
        const participantInfo = participantReferenceList.find(participantReference => {
          return this.verifyIfIsAllowedToSendMessageToUser(participantReference.safeaUserId)
            && participantReference.safeaUserId === participantSafeaUserId;
        });

        if (!participantInfo) {
          continue;
        }

        selectedContacts.push(this.parseParticipantToMessageContact(participantInfo));
      };

      this.handleSendMessage(selectedContacts);
    },
    handleSendMessage(selectedContacts) {
      this.$nuxt.$emit('openMessageModal', {
        contexts         : [this.getCourseContext()],
        defaultBoxFilter : 'inbox',
        selectedContacts
      });
    },
    verifyIfIsAllowedToSendMessageToUser(safeaUserId) {
      return safeaUserId !== this.getUserId;
    }
  }
};
